import { Injectable, signal, computed } from '@angular/core';
import { IContract, IContractApprovalConf, IContractSignatoryConf, SIGNATORY_TYPE } from '../models/contract-management.model';
import { IMilestone } from '../../@core/models/model';

export interface EditContractData {
    parent?: any;
    contractId: string;
    contract: IContract & { containsForm: boolean };
    currentRole: string;
    projectId: string;
    emptyDocument: boolean;
    currentStepNumber: number;
    approvalConf: IContractApprovalConf;
    allApprovers: any[];
    isOwner: boolean;
    isEditor: boolean;
    permissions: {
        canView: boolean;
        canEdit: boolean;
        canComment: boolean;
        restricted: boolean;
    };
    signatoryConf: IContractSignatoryConf;
    allSignatories: any[];
    disableAddSignatories: boolean;
    signatoryType: SIGNATORY_TYPE;
    milestones: IMilestone[];
}

@Injectable({
    providedIn: 'root'
})
export class EditContractDataService {
    // Signals for reactive state management
    private dataSignal = signal<EditContractData | null>(null);

    // Computed signals for specific properties
    readonly data = this.dataSignal.asReadonly();
    readonly contractId = computed(() => this.dataSignal()?.contractId || '');
    readonly contract = computed(() => this.dataSignal()?.contract);
    readonly currentRole = computed(() => this.dataSignal()?.currentRole || '');
    readonly projectId = computed(() => this.dataSignal()?.projectId || '');
    readonly emptyDocument = computed(() => this.dataSignal()?.emptyDocument ?? true);
    readonly currentStepNumber = computed(() => this.dataSignal()?.currentStepNumber || 0);
    readonly approvalConf = computed(() => this.dataSignal()?.approvalConf);
    readonly allApprovers = computed(() => this.dataSignal()?.allApprovers || []);
    readonly isOwner = computed(() => this.dataSignal()?.isOwner ?? false);
    readonly isEditor = computed(() => this.dataSignal()?.isEditor ?? false);
    readonly permissions = computed(() => this.dataSignal()?.permissions);
    readonly signatoryConf = computed(() => this.dataSignal()?.signatoryConf);
    readonly allSignatories = computed(() => this.dataSignal()?.allSignatories || []);
    readonly disableAddSignatories = computed(() => this.dataSignal()?.disableAddSignatories ?? false);
    readonly signatoryType = computed(() => this.dataSignal()?.signatoryType);
    readonly milestones = computed(() => this.dataSignal()?.milestones || []);

    setData(data: EditContractData) {
        this.dataSignal.set(data);
    }

    updateData(partialData: Partial<EditContractData>) {
        const currentData = this.dataSignal();
        if (currentData) {
            this.dataSignal.set({ ...currentData, ...partialData });
        }
    }

    getData() {
        return this.dataSignal();
    }
}

